import { GeoUpdatePayload } from 'src/components/dispatch/detailPanel/DispatchDetailPanel';
import { DispatchCard, UnassignedItem } from 'src/entities/dispatch';
import { VehicleRowGroup } from 'src/pages/ConvenienceDispatches/adapter';

/**
 * 同一住所の判定に使う「画面に出ている地点」の母集団。
 * 呼び出し側は、その画面が実際に描いているもの（盤面の行と未割当リストの items）を渡す。
 * 期間外・過去・未来の案件は渡らないので、見えていない伝票を巻き込まない。
 */
export type SameAddressPlaceSource = {
  /** 表示中の割当済み盤面 */
  rowGroups: readonly VehicleRowGroup[];
  /** 表示中の未割当リスト */
  unassignedItems: readonly UnassignedItem[];
};

export type SameAddressLegRuns = {
  /** 一括更新で API へ渡す legRun ID。先頭は編集した地点のぶん */
  legRunIds: number[];
  /** 同じ住所を持つ**他の**地点の件数。0 なら確認ダイアログを出さずに更新する */
  otherPlaceCount: number;
};

/** 画面に出ている 1 地点。住所とその地点が束ねている legRun だけを見る */
type Place = { address: string | undefined; legRunIds: readonly number[] };

/**
 * 割当済みカードのうち、その side の「地点」として画面に出ているものの住所。
 *
 * - 降地 (unloading) は降地カードの storeAddress
 * - 積地 (loading) は積地カードの address
 *
 * Why not 降地カードの originAddress も積地として数える: その legRun の積地は同じ行の
 * 積地カードが表しているので、両方数えると 1 つの地点を 2 件として数えてしまう
 * （確認ダイアログの「他の地点が N 件」が実際の地点数とずれる）。
 *
 * 編集を許す降地カードの条件は DispatchDetailPanel の geoEditSide と同じ matched /
 * template に揃える。fr-template の legRunIds は固定ルートの停車地名に名前一致するだけの
 * 候補集合なので、巻き込むと無関係な案件の座標が書き換わる。
 */
function placeOfCard(card: DispatchCard, side: GeoUpdatePayload['side']): Place | null {
  if (side === 'loading') {
    return card.type === 'loading' ? { address: card.address, legRunIds: card.legRunIds ?? [] } : null;
  }
  if (card.type !== 'delivery') return null;
  if (card.status !== 'matched' && card.status !== 'template') return null;
  return { address: card.storeAddress, legRunIds: card.legRunIds ?? [] };
}

/**
 * 座標を直したとき、同じ住所を持つ他の地点も一緒に直すための legRun を集める
 * (log_web_issues#338 C14 / サンプル route-planner MatchingPresenter/index.tsx:2501-2544)。
 *
 * Why 3 画面で 1 本に: /long-haul・カード配車・/map のどれも同じ詳細パネルから呼ぶ。
 * 画面ごとに書くと、片方だけ条件を直したときに確認ダイアログの件数と実際に更新される
 * 件数が食い違う（押す前に見せた数字が嘘になる）。
 *
 * 住所は**完全一致**で突き合わせる。API 側の Place 更新も (name, address) の完全一致
 * なので、ここだけ緩めると「画面では巻き込んだのに地点マスタは更新されない」地点が出る。
 * 空文字・undefined の住所はどちらの側も一致させない（住所未設定どうしが全部まとまる）。
 */
export function collectSameAddressLegRuns(
  payload: GeoUpdatePayload,
  source: SameAddressPlaceSource,
): SameAddressLegRuns {
  const targetIds = [...payload.legRunIds];
  const address = payload.address;
  if (!address) return { legRunIds: targetIds, otherPlaceCount: 0 };

  const targetIdSet = new Set(targetIds);
  const places: Place[] = [
    ...source.rowGroups.flatMap((group) => group.vehicle.cards
      .map((card) => placeOfCard(card, payload.side))
      .filter((place): place is Place => place !== null)),
    ...source.unassignedItems.map((item) => ({
      address: payload.side === 'loading' ? item.originAddress : item.storeAddress,
      legRunIds: item.legRunIds ?? [],
    })),
  ];

  const otherIds: number[] = [];
  const seen = new Set<number>();
  let otherPlaceCount = 0;
  places.forEach((place) => {
    if (place.address !== address || place.legRunIds.length === 0) return;
    // 編集した地点そのもの。legRun が 1 つでも重なれば同じ地点とみなす
    // （1 枚のカードにマージされた legRun の一部が別カードにも出ている場合を含む）
    if (place.legRunIds.some((id) => targetIdSet.has(id))) return;
    otherPlaceCount += 1;
    place.legRunIds.forEach((id) => {
      if (seen.has(id)) return;
      seen.add(id);
      otherIds.push(id);
    });
  });

  return { legRunIds: [...targetIds, ...otherIds], otherPlaceCount };
}
